import clsx from "clsx";
import { Pencil, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import Markdown from "@/components/chat/Markdown";
import { Button, Checkbox, ErrorText, Input, Modal, Select, Spinner, Textarea } from "@/components/ui";
import { formatValue, spaceApi, type Action, type Cell, type Field, type View } from "@/lib/spaces";

type Of<T extends View["type"]> = Extract<View, { type: T }>;
type OnChanged = (batch: string | null, message: string) => void;
type OnPrompt = (text: string, asChat?: boolean) => void;

/** A row action's prompt with {column} filled in from the row; {id} is the record id. Unknown names stay as they are. */
function fill(prompt: string, columns: string[], row: Cell[]): string {
  const at = new Map(columns.map((c, i) => [c.toLowerCase(), i]));
  if (at.has("_id") && !at.has("id")) at.set("id", at.get("_id")!);
  return prompt.replace(/\{([^{}]+)\}/g, (m, name: string) => {
    const i = at.get(name.trim().toLowerCase());
    return i === undefined ? m : String(row[i] ?? "");
  });
}

function RowActions({ actions, columns, row, onPrompt }: { actions: Action[]; columns: string[]; row: Cell[]; onPrompt: OnPrompt }) {
  return (
    <>
      {actions.map((a, i) => (
        <button key={i} type="button" onClick={() => onPrompt(fill(a.prompt, columns, row), a.chat)} className="rounded-lg px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-950">
          {a.label}
        </button>
      ))}
    </>
  );
}

const SERIES_COLORS = ["#6366f1", "#10b981", "#f59e0b", "#f43f5e", "#0ea5e9"];

function num(v: Cell | undefined): number {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const n = parseFloat(v.replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function trunc(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function fmtNum(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="text-sm text-zinc-500">{children}</p>;
}

// ---------- stat / progress ----------

function Stat({ view }: { view: Of<"stat"> }) {
  const value = view.data?.value ?? null;
  const text = value === null ? "" : formatValue(value, { type: "number", unit: view.unit }, view.decimals);
  return <div className="text-3xl font-semibold tabular-nums">{text || "—"}</div>;
}

function Progress({ view }: { view: Of<"progress"> }) {
  const value = view.data?.value ?? null;
  const target = view.data?.target ?? view.target ?? null;
  const f = { type: "number" as const, unit: view.unit };
  const v = value === null ? null : num(value);
  const t = target === null ? null : num(target);
  if (v === null) return <div className="text-3xl font-semibold">—</div>;
  if (t === null || t <= 0) return <div className="text-3xl font-semibold tabular-nums">{formatValue(v, f, view.decimals)}</div>;
  const pct = (v / t) * 100;
  const bar = pct > 100 ? "bg-red-500" : pct > 90 ? "bg-amber-500" : "bg-indigo-500";
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm">
          <span className="text-2xl font-semibold tabular-nums">{formatValue(v, { type: "number" }, view.decimals)}</span>
          <span className="text-zinc-500">
            {" "}
            of {formatValue(t, f, view.decimals)}
          </span>
        </span>
        <span className="text-sm font-medium tabular-nums text-zinc-600 dark:text-zinc-400">{Math.round(pct)}%</span>
      </div>
      <div className="h-2.5 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
        <div className={clsx("h-full rounded-full transition-all", bar)} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
      </div>
    </div>
  );
}

// ---------- chart ----------

function niceStep(raw: number): number {
  if (raw <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * p;
}

/** The width of an element, kept up to date, so a chart can draw at its real size (text stays the same size). */
function useWidth(): [(el: HTMLDivElement | null) => void, number] {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.round(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, width];
}

/** Roughly how wide one character of 11px label text is. */
const CHAR_W = 6.2;

/** Bars lying down, labels on the left: used when the labels are too long to fit under upright bars. */
function HBars({ W, labels, names, series, fmt }: { W: number; labels: string[]; names: string[]; series: number[][]; fmt: (n: number) => string }) {
  const row = 22 + (series.length - 1) * 10;
  const H = row * labels.length;
  const labelW = Math.min(W * 0.4, Math.max(...labels.map((l) => l.length)) * CHAR_W + 8);
  const valueW = Math.max(...series.flat().map((v) => fmt(v).length)) * CHAR_W + 8;
  const plotW = Math.max(20, W - labelW - valueW);
  const max = Math.max(0, ...series.flat()) || 1;
  const chars = Math.floor((labelW - 8) / CHAR_W);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img">
      {labels.map((l, i) => {
        const bh = (row - 6) / series.length;
        return (
          <g key={i}>
            <text x={labelW - 8} y={row * i + row / 2} textAnchor="end" dominantBaseline="middle" fontSize={11} className="fill-zinc-500">
              <title>{l}</title>
              {trunc(l, chars)}
            </text>
            {series.map((s, si) => (
              <rect key={si} x={labelW} y={row * i + 3 + bh * si} width={Math.max(1, (Math.max(0, s[i]) / max) * plotW)} height={Math.max(1, bh - 1)} rx={1.5} fill={SERIES_COLORS[si % SERIES_COLORS.length]}>
                <title>{`${l}: ${fmt(s[i])}${series.length > 1 ? ` (${names[si]})` : ""}`}</title>
              </rect>
            ))}
            {series.length === 1 && (
              <text x={labelW + (Math.max(0, series[0][i]) / max) * plotW + 4} y={row * i + row / 2} dominantBaseline="middle" fontSize={11} className="fill-zinc-500 tabular-nums">
                {fmt(series[0][i])}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function Chart({ view }: { view: Of<"chart"> }) {
  const [box, width] = useWidth();
  const data = view.data;
  if (!data || data.rows.length === 0 || data.columns.length < 2) return <Empty>No data yet</Empty>;
  const kind = view.chart ?? "bar";
  const labels = data.rows.map((r) => String(r[0] ?? ""));
  const names = data.columns.slice(1);
  const series = names.map((_, i) => data.rows.map((r) => num(r[i + 1])));
  const fmt = (n: number) => (view.unit ? `${fmtNum(n)} ${view.unit}` : fmtNum(n));
  const legend =
    kind === "pie" ? null : names.length > 1 ? (
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-600 dark:text-zinc-400">
        {names.map((n, i) => (
          <span key={i} className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: SERIES_COLORS[i % SERIES_COLORS.length] }} />
            {n}
          </span>
        ))}
      </div>
    ) : null;

  if (kind === "pie") {
    const vals = series[0].map((v) => Math.max(0, v));
    const total = vals.reduce((a, b) => a + b, 0);
    if (total <= 0) return <Empty>No data yet</Empty>;
    const cx = 90;
    const cy = 90;
    const r = 80;
    let angle = -Math.PI / 2;
    const slices = vals.map((v, i) => {
      const frac = v / total;
      const a0 = angle;
      angle += frac * Math.PI * 2;
      const color = SERIES_COLORS[i % SERIES_COLORS.length];
      const title = `${labels[i]}: ${fmt(series[0][i])}`;
      if (frac >= 0.9999) return <circle key={i} cx={cx} cy={cy} r={r} fill={color}><title>{title}</title></circle>;
      if (frac <= 0) return null;
      const x0 = cx + r * Math.cos(a0);
      const y0 = cy + r * Math.sin(a0);
      const x1 = cx + r * Math.cos(angle);
      const y1 = cy + r * Math.sin(angle);
      return (
        <path key={i} d={`M${cx},${cy} L${x0},${y0} A${r},${r} 0 ${frac > 0.5 ? 1 : 0} 1 ${x1},${y1} Z`} fill={color} stroke="currentColor" strokeWidth={1} className="text-white dark:text-zinc-900">
          <title>{title}</title>
        </path>
      );
    });
    return (
      <div className="flex flex-wrap items-center gap-4">
        <svg viewBox="0 0 180 180" className="h-[180px] w-[180px] max-w-full shrink-0" role="img">
          {slices}
        </svg>
        <ul className="min-w-0 flex-1 space-y-1 text-xs text-zinc-600 dark:text-zinc-400">
          {labels.map((l, i) => (
            <li key={i} className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: SERIES_COLORS[i % SERIES_COLORS.length] }} />
              <span className="truncate">{trunc(l, 24)}</span>
              <span className="ml-auto shrink-0 tabular-nums">{Math.round((vals[i] / total) * 100)}%</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // bar / line
  const W = Math.max(240, width || 400);
  const longest = Math.max(...labels.map((l) => l.length));
  const fits = Math.floor((W - 44) / labels.length / CHAR_W);
  if (kind === "bar" && longest > fits && labels.length <= 20) {
    return (
      <div ref={box}>
        <HBars W={W} labels={labels} names={names} series={series} fmt={fmt} />
        {legend}
      </div>
    );
  }
  const H = 180;
  const left = 38;
  const right = 6;
  const top = 8;
  const bottom = 24;
  const all = series.flat();
  const dataMax = Math.max(0, ...all);
  const dataMin = Math.min(0, ...all);
  const step = niceStep((dataMax - dataMin || 1) / 4);
  const yMax = Math.ceil(dataMax / step) * step || step;
  const yMin = Math.floor(dataMin / step) * step;
  const ticks: number[] = [];
  for (let t = yMin; t <= yMax + step / 2; t += step) ticks.push(t);
  const plotW = W - left - right;
  const plotH = H - top - bottom;
  const y = (v: number) => top + plotH - ((v - yMin) / (yMax - yMin || 1)) * plotH;
  const n = labels.length;
  const band = plotW / n;
  const every = Math.max(1, Math.ceil(n / Math.max(1, Math.floor(plotW / 44))));
  const x = (i: number) => left + band * i + band / 2;

  return (
    <div ref={box}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" role="img">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={left} x2={W - right} y1={y(t)} y2={y(t)} className="stroke-zinc-200 dark:stroke-zinc-800" strokeWidth={1} />
            <text x={left - 4} y={y(t)} textAnchor="end" dominantBaseline="middle" fontSize={11} className="fill-zinc-500">
              {fmtNum(t)}
            </text>
          </g>
        ))}
        {kind === "bar"
          ? series.map((s, si) => {
              const groupW = band * 0.8;
              const bw = groupW / series.length;
              return s.map((v, i) => {
                const bx = left + band * i + (band - groupW) / 2 + bw * si;
                const y0 = y(0);
                const y1 = y(v);
                return (
                  <rect key={`${si}-${i}`} x={bx} y={Math.min(y0, y1)} width={Math.max(1, bw - 1)} height={Math.max(0.5, Math.abs(y0 - y1))} rx={1.5} fill={SERIES_COLORS[si % SERIES_COLORS.length]}>
                    <title>{`${labels[i]}: ${fmt(v)}${series.length > 1 ? ` (${names[si]})` : ""}`}</title>
                  </rect>
                );
              });
            })
          : series.map((s, si) => {
              const color = SERIES_COLORS[si % SERIES_COLORS.length];
              return (
                <g key={si}>
                  <polyline points={s.map((v, i) => `${x(i)},${y(v)}`).join(" ")} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                  {s.map((v, i) => (
                    <circle key={i} cx={x(i)} cy={y(v)} r={3} fill={color}>
                      <title>{`${labels[i]}: ${fmt(v)}${series.length > 1 ? ` (${names[si]})` : ""}`}</title>
                    </circle>
                  ))}
                </g>
              );
            })}
        {labels.map((l, i) =>
          i % every === 0 ? (
            <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize={11} className="fill-zinc-500">
              {trunc(l, Math.max(3, Math.floor((band * every - 4) / CHAR_W)))}
            </text>
          ) : null,
        )}
      </svg>
      {legend}
    </div>
  );
}

// ---------- table ----------

function DataTable({ columns, rows, actions, onPrompt }: { columns: string[]; rows: Cell[][]; actions?: Action[]; onPrompt: OnPrompt }) {
  return (
    <div className="max-h-72 overflow-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th key={i} className={clsx("sticky top-0 whitespace-nowrap bg-white px-2 py-1.5 text-xs font-medium text-zinc-500 dark:bg-zinc-900", rows.some((r) => typeof r[i] === "number") ? "text-right" : "text-left")}>
                {c}
              </th>
            ))}
            {actions?.length ? <th className="sticky top-0 w-0 bg-white dark:bg-zinc-900" /> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className="border-t border-zinc-100 dark:border-zinc-800">
              {columns.map((_, ci) => (
                <td key={ci} className={clsx("px-2 py-1.5", typeof r[ci] === "number" && "text-right tabular-nums")}>
                  {typeof r[ci] === "number" ? fmtNum(r[ci] as number) : (r[ci] ?? "")}
                </td>
              ))}
              {actions?.length ? (
                <td className="whitespace-nowrap px-1 py-1 text-right">
                  <RowActions actions={actions} columns={columns} row={r} onPrompt={onPrompt} />
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TableView({ view, onPrompt }: { view: Of<"table">; onPrompt: OnPrompt }) {
  const d = view.data;
  if (!d || d.rows.length === 0) return <Empty>No rows</Empty>;
  return <DataTable columns={d.columns} rows={d.rows} actions={view.actions} onPrompt={onPrompt} />;
}

// ---------- calendar ----------

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const LEVELS = ["fill-zinc-100 dark:fill-zinc-800", "fill-indigo-200 dark:fill-indigo-900", "fill-indigo-300 dark:fill-indigo-700", "fill-indigo-500 dark:fill-indigo-500", "fill-indigo-700 dark:fill-indigo-300"];

function CalendarView({ view }: { view: Of<"calendar"> }) {
  const days = Math.max(1, view.data?.days ?? view.days ?? 90);
  const cells = useMemo(() => {
    const byDate = new Map<string, number>();
    for (const r of view.data?.rows ?? []) {
      const k = String(r[0] ?? "").slice(0, 10);
      byDate.set(k, (byDate.get(k) ?? 0) + num(r[1]));
    }
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const start = new Date(today);
    start.setDate(start.getDate() - (days - 1));
    const lead = (start.getDay() + 6) % 7;
    const out: { key: string; value: number; col: number; row: number; month: number; day: number }[] = [];
    for (let i = 0; i < days; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const idx = i + lead;
      const key = dateKey(d);
      out.push({ key, value: byDate.get(key) ?? 0, col: Math.floor(idx / 7), row: idx % 7, month: d.getMonth(), day: d.getDate() });
    }
    return out;
  }, [view.data, days]);
  const max = Math.max(0, ...cells.map((c) => c.value));
  const level = (v: number) => (v <= 0 || max <= 0 ? 0 : Math.min(4, Math.ceil((v / max) * 4)));
  const size = 11;
  const gap = 3;
  const pitch = size + gap;
  const cols = Math.max(...cells.map((c) => c.col)) + 1;
  const topPad = 12;
  const monthLabels: { col: number; text: string }[] = [];
  let lastMonth = -1;
  for (const c of cells) {
    if (c.month !== lastMonth && (c.day <= 7 || monthLabels.length === 0)) {
      if (!monthLabels.length || c.col - monthLabels[monthLabels.length - 1].col >= 3) monthLabels.push({ col: c.col, text: new Date(2000, c.month, 1).toLocaleString(undefined, { month: "short" }) });
      lastMonth = c.month;
    }
  }
  const width = cols * pitch;
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${topPad + 7 * pitch}`} style={{ width: Math.max(width, 0), minWidth: "100%", maxWidth: width * 2 }} className="h-auto" role="img">
        {monthLabels.map((m, i) => (
          <text key={i} x={m.col * pitch} y={9} fontSize={9} className="fill-zinc-500">
            {m.text}
          </text>
        ))}
        {cells.map((c) => (
          <rect key={c.key} x={c.col * pitch} y={topPad + c.row * pitch} width={size} height={size} rx={2} className={LEVELS[level(c.value)]}>
            <title>{`${c.key}: ${fmtNum(c.value)}`}</title>
          </rect>
        ))}
      </svg>
    </div>
  );
}

// ---------- field inputs (forms + record editing) ----------

type FormValues = Record<string, string | boolean>;

function FieldInput({ field, value, onChange, disabled, compact }: { field: Field; value: string | boolean; onChange: (v: string | boolean) => void; disabled?: boolean; compact?: boolean }) {
  const str = typeof value === "string" ? value : "";
  const set = (e: { target: { value: string } }) => onChange(e.target.value);
  const placeholder = compact ? field.label : field.type === "date" ? "today" : field.type === "datetime" ? "now" : undefined;
  let input: ReactNode;
  switch (field.type) {
    case "bool":
      return <Checkbox label={field.label} checked={value === true} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />;
    case "longtext":
      input = <Textarea rows={2} value={str} onChange={set} disabled={disabled} />;
      break;
    case "number":
    case "money":
    case "integer":
      input = <Input type="text" inputMode="decimal" value={str} onChange={set} disabled={disabled} placeholder={placeholder} aria-label={field.label} />;
      break;
    case "date":
      input = <Input type="date" value={str} onChange={set} disabled={disabled} aria-label={field.label} />;
      break;
    case "datetime":
      input = <Input type="datetime-local" value={str} onChange={set} disabled={disabled} aria-label={field.label} />;
      break;
    case "time":
      input = <Input type="time" value={str} onChange={set} disabled={disabled} aria-label={field.label} />;
      break;
    case "choice":
      input = (
        <Select value={str} onChange={set} disabled={disabled} aria-label={field.label}>
          <option value="">{compact ? field.label : ""}</option>
          {(field.options ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </Select>
      );
      break;
    default:
      input = <Input type="text" value={str} onChange={set} disabled={disabled} placeholder={placeholder} aria-label={field.label} />;
  }
  if (compact) return <div className="min-w-0 flex-1">{input}</div>;
  const hint = field.type === "date" || field.type === "datetime" ? "Empty = " + (field.type === "date" ? "today" : "now") : undefined;
  return (
    <label className="block space-y-1">
      <span className="text-sm font-medium">
        {field.label}
        {field.unit && <span className="font-normal text-zinc-500"> ({field.unit})</span>}
        {field.required && " *"}
      </span>
      {input}
      {hint && <span className="block text-xs text-zinc-500">{hint}</span>}
    </label>
  );
}

function FormView({ spaceId, view, onChanged }: { spaceId: string; view: Of<"form">; onChanged: OnChanged }) {
  const fields = view.data?.fields ?? [];
  const collection = view.data?.collection ?? view.collection;
  const [values, setValues] = useState<FormValues>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const ref = useRef<HTMLFormElement>(null);
  const compact = fields.length > 0 && fields.length <= 2 && fields.every((f) => f.type === "text" || f.type === "number" || f.type === "money" || f.type === "integer");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const out: Record<string, unknown> = {};
    for (const f of fields) {
      const v = values[f.name];
      if (f.type === "bool") out[f.name] = v === true;
      else if (typeof v === "string" && v.trim() !== "") out[f.name] = v;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await spaceApi.addRecord(spaceId, collection, out);
      setValues({});
      onChanged(r.batch, "Added");
      setBusy(false);
      requestAnimationFrame(() => ref.current?.querySelector<HTMLElement>("input,textarea,select")?.focus());
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  if (fields.length === 0) return <Empty>No fields</Empty>;
  const button = (
    <Button type="submit" disabled={busy} className={compact ? "shrink-0" : "w-full sm:w-auto"}>
      {busy && <Spinner size={14} />}
      {view.submit || "Add"}
    </Button>
  );
  return (
    <form ref={ref} onSubmit={submit} className="space-y-2">
      {compact ? (
        <div className="flex gap-2">
          {fields.map((f) => (
            <FieldInput key={f.name} field={f} compact value={values[f.name] ?? ""} onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))} disabled={busy} />
          ))}
          {button}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {fields.map((f) => (
              <div key={f.name} className={clsx(f.type === "longtext" && "sm:col-span-2")}>
                <FieldInput field={f} value={values[f.name] ?? (f.type === "bool" ? false : "")} onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))} disabled={busy} />
              </div>
            ))}
          </div>
          {button}
        </>
      )}
      <ErrorText error={error} />
    </form>
  );
}

// ---------- records ----------

function initialValue(f: Field, cell: Cell | undefined): string | boolean {
  if (f.type === "bool") return !!cell && cell !== "0";
  if (cell === null || cell === undefined) return "";
  if (f.type === "datetime") return String(cell).replace(" ", "T").slice(0, 16);
  return String(cell);
}

function EditModal({ spaceId, collection, id, fields, cells, onClose, onChanged }: { spaceId: string; collection: string; id: number; fields: Field[]; cells: Cell[]; onClose: () => void; onChanged: OnChanged }) {
  const [values, setValues] = useState<FormValues>(() => Object.fromEntries(fields.map((f, i) => [f.name, initialValue(f, cells[i])])));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    const out: Record<string, unknown> = {};
    for (const f of fields) {
      const v = values[f.name];
      if (f.type === "bool") out[f.name] = v === true;
      else out[f.name] = typeof v === "string" && v.trim() !== "" ? v : null;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await spaceApi.updateRecord(spaceId, collection, id, out);
      onChanged(r.batch, "Saved");
      onClose();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title="Edit">
      <form onSubmit={save} className="space-y-3">
        {fields.map((f) => (
          <FieldInput key={f.name} field={f} value={values[f.name] ?? ""} onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))} disabled={busy} />
        ))}
        <ErrorText error={error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy && <Spinner size={14} />}
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function Records({ spaceId, view, onChanged, onPrompt }: { spaceId: string; view: Of<"records">; onChanged: OnChanged; onPrompt: OnPrompt }) {
  const d = view.data;
  const [editing, setEditing] = useState<{ id: number; cells: Cell[] } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  if (!d || d.rows.length === 0) return <Empty>Nothing here yet</Empty>;
  const fields = d.fields;

  async function del(id: number) {
    setBusyId(id);
    setError(null);
    try {
      const r = await spaceApi.deleteRecord(spaceId, d!.collection, id);
      onChanged(r.batch, "Deleted");
    } catch (err) {
      setError(err);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-2">
      <div className="max-h-80 overflow-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              {fields.map((f) => (
                <th key={f.name} className={clsx("sticky top-0 whitespace-nowrap bg-white px-2 py-1.5 text-xs font-medium text-zinc-500 dark:bg-zinc-900", f.type === "number" || f.type === "money" || f.type === "integer" ? "text-right" : "text-left")}>
                  {f.label}
                </th>
              ))}
              <th className="sticky top-0 w-0 bg-white dark:bg-zinc-900" />
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r) => {
              const id = Number(r[0]);
              const cells = r.slice(1);
              return (
                <tr key={id} className="group border-t border-zinc-100 dark:border-zinc-800">
                  {fields.map((f, i) => (
                    <td key={f.name} className={clsx("px-2 py-1.5", f.type === "number" || f.type === "money" || f.type === "integer" ? "text-right tabular-nums" : f.type === "longtext" ? "max-w-xs truncate" : "")}>
                      {formatValue(cells[i] ?? null, f)}
                    </td>
                  ))}
                  <td className="whitespace-nowrap px-1 py-1 text-right">
                    {view.actions?.length ? <RowActions actions={view.actions} columns={d.columns} row={r} onPrompt={onPrompt} /> : null}
                    <span className="opacity-60 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
                    <button type="button" aria-label="Edit" disabled={busyId === id} onClick={() => setEditing({ id, cells })} className="inline-flex h-8 w-8 items-center justify-center rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800">
                      <Pencil size={14} />
                    </button>
                    <button type="button" aria-label="Delete" disabled={busyId === id} onClick={() => void del(id)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950">
                      {busyId === id ? <Spinner size={14} /> : <Trash2 size={14} />}
                    </button>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ErrorText error={error} />
      {editing && <EditModal spaceId={spaceId} collection={d.collection} id={editing.id} fields={fields} cells={editing.cells} onClose={() => setEditing(null)} onChanged={onChanged} />}
    </div>
  );
}

// ---------- checklist ----------

type Item = { id: number; label: string; done: boolean };

function toItems(rows: Cell[][] | undefined): Item[] {
  return (rows ?? []).map((r) => ({ id: Number(r[0]), label: String(r[1] ?? ""), done: !!r[2] && r[2] !== "0" }));
}

function Checklist({ spaceId, view, onChanged }: { spaceId: string; view: Of<"checklist">; onChanged: OnChanged }) {
  const [items, setItems] = useState<Item[]>(() => toItems(view.data?.rows));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => setItems(toItems(view.data?.rows)), [view.data]);
  const collection = view.data?.collection ?? view.collection;
  const doneCount = items.filter((i) => i.done).length;

  async function toggle(item: Item) {
    const next = !item.done;
    setError(null);
    setItems((s) => s.map((i) => (i.id === item.id ? { ...i, done: next } : i)));
    try {
      await spaceApi.updateRecord(spaceId, collection, item.id, { [view.done_field]: next });
      onChanged(null, ""); // no toast: ticking is undone by ticking again
    } catch (err) {
      setError(err);
      setItems((s) => s.map((i) => (i.id === item.id ? { ...i, done: item.done } : i)));
    }
  }

  async function clear() {
    setPending(true);
    setError(null);
    try {
      const r = await spaceApi.clearChecklist(spaceId, view.id);
      onChanged(r.batch, `Cleared ${r.deleted} item${r.deleted === 1 ? "" : "s"}`);
    } catch (err) {
      setError(err);
    } finally {
      setPending(false);
    }
  }

  // ticked items sink to the bottom; stable otherwise
  const ordered = [...items.filter((i) => !i.done), ...items.filter((i) => i.done)];
  return (
    <div className="space-y-2">
      {items.length === 0 ? (
        <Empty>Nothing here yet</Empty>
      ) : (
        <ul>
          {ordered.map((i) => (
            <li key={i.id}>
              <label className="flex min-h-10 cursor-pointer items-center gap-3 rounded-lg px-1 py-1.5 active:bg-zinc-100 dark:active:bg-zinc-800">
                <input type="checkbox" checked={i.done} onChange={() => void toggle(i)} className="h-5 w-5 shrink-0 accent-indigo-600" />
                <span className={clsx("min-w-0 break-words text-sm", i.done && "text-zinc-400 line-through dark:text-zinc-500")}>{i.label}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
      {doneCount > 0 && (
        <Button variant="secondary" size="sm" onClick={() => void clear()} disabled={pending}>
          {pending && <Spinner size={12} />}
          Clear ticked
        </Button>
      )}
      <ErrorText error={error} />
    </div>
  );
}

// ---------- card ----------

export default function ViewCard({ spaceId, view, onPrompt, onChanged }: { spaceId: string; view: View; onPrompt: OnPrompt; onChanged: OnChanged }) {
  let body: ReactNode;
  if (view.error) {
    body = <p className="text-xs text-red-600 dark:text-red-400">{view.error}</p>;
  } else {
    switch (view.type) {
      case "stat":
        body = <Stat view={view} />;
        break;
      case "progress":
        body = <Progress view={view} />;
        break;
      case "chart":
        body = <Chart view={view} />;
        break;
      case "table":
        body = <TableView view={view} onPrompt={onPrompt} />;
        break;
      case "calendar":
        body = <CalendarView view={view} />;
        break;
      case "records":
        body = <Records spaceId={spaceId} view={view} onChanged={onChanged} onPrompt={onPrompt} />;
        break;
      case "checklist":
        body = <Checklist spaceId={spaceId} view={view} onChanged={onChanged} />;
        break;
      case "form":
        body = <FormView spaceId={spaceId} view={view} onChanged={onChanged} />;
        break;
      case "note":
        body = <Markdown text={view.text} />;
        break;
      case "prompts":
        body = (
          <div className="flex flex-wrap gap-2">
            {view.buttons.map((b, i) => (
              <Button key={i} variant="secondary" size="sm" onClick={() => onPrompt(b.prompt, b.chat)}>
                {b.label}
              </Button>
            ))}
          </div>
        );
        break;
    }
  }
  return (
    <section className="min-w-0 rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
      {view.label && <h3 className="mb-2 text-xs font-semibold text-zinc-500 dark:text-zinc-400">{view.label}</h3>}
      {body}
    </section>
  );
}
